import { Link } from "react-router-dom";
import { ArrowLeft, MapPin, Banknote, CreditCard, Wallet, Loader2, Plus, ChevronRight, AlertTriangle, BadgePercent, Coins, X } from "lucide-react";
import { api, inr2 } from "../../lib/api";
import { t } from "../../lib/i18n";
import { AddrSummary, PayOpt, Line, CheckoutAddressDrawer } from "./_partials/CheckoutPartials";
import AddressPickerModal from "../../components/AddressPickerModal";
import CouponPickerModal from "./_partials/CouponPickerModal";
import { CoinsRedeemBlock } from "./_partials/CartPickers";
import { useIsBlinkit } from "../../lib/storefrontTheme";
import { useCheckoutController } from "./controllers/useCheckoutController";
import BkCheckoutView from "./blinkit/BkCheckoutView";
import DeliveryOptions from "../../components/lse/DeliveryOptions";
import { deliveryBlocking } from "../../lib/lse";
import { ShopBreakdown } from "../../components/MultiShop";

export default function CheckoutPage() {
    return useIsBlinkit() ? <BkCheckoutView /> : <ClassicCheckoutPage />;
}

function ClassicCheckoutPage() {
    const {
        lang, razorpayMock, codAllowed, addrList, selectedAddrId, setSelectedAddrId, showPicker, setShowPicker, showAddNew,
        setShowAddNew, payment, setPayment, useWallet, setUseWallet, walletBalance, notes, setNotes, busy, liveCart,
        couponInput, setCouponInput, couponDraft, setCouponDraft, showCouponPicker, setShowCouponPicker, availCoupons,
        couponPickerBusy, coinsBalance, coinsConfig, coinsToRedeem, setCoinsToRedeem, loadAddrs, liveTotals, grandTotal,
        coinMax, coinMin, handleAddNew, selectedAddr, showSetLocation, setShowSetLocation, locationNeeded,
        openCouponPicker, applyCouponFromPicker, placeOrder, lse, delivery, chooseDelivery,
    } = useCheckoutController();
    const lseBlock = deliveryBlocking(lse);

    return (
        <div className="pb-32 lg:pb-12">
            <header className="px-4 pt-5 pb-4 flex items-center gap-3 bg-card sticky top-0 z-10 border-b border-border">
                <Link to="/cart" className="p-1 -ml-1"><ArrowLeft size={22} /></Link>
                <h1 className="text-base font-bold">Checkout</h1>
            </header>

            {/* Tablet+ split: address + payment + notes on the left,
                rewards + live order summary on the right so iPad/desktop
                doesn't show a single 1000px-wide column. */}
            <div className="lg:grid lg:grid-cols-[1.4fr_1fr] lg:gap-4 lg:px-4 lg:pt-4">
            <div className="lg:space-y-4">

            {/* Address */}
            <section className="m-4 lg:m-0 bg-card rounded-2xl p-4 border border-border">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2"><MapPin size={18} className="text-primary" /><h3 className="font-bold text-sm">Delivery address</h3></div>
                    {addrList.length > 0 && (
                        <button data-testid="change-address" onClick={() => setShowPicker(true)} className="text-xs font-semibold text-primary">Change</button>
                    )}
                </div>

                {selectedAddr ? (
                    <AddrSummary a={selectedAddr} />
                ) : (
                    <button
                        data-testid="add-first-address"
                        onClick={() => setShowAddNew(true)}
                        className="w-full border border-dashed border-primary/40 text-primary rounded-2xl py-4 font-semibold inline-flex items-center justify-center gap-2"
                    >
                        <Plus size={18} /> Add a delivery address
                    </button>
                )}
            </section>

            {/* Location Service Engine tenants only (renders nothing otherwise). */}
            {lse && <div className="m-4 lg:m-0"><DeliveryOptions lse={lse} value={delivery} onChange={chooseDelivery} /></div>}

            {/* Multi-shop checkout only (renders nothing otherwise): what each shop's order comes to. */}
            <ShopBreakdown cart={liveCart} className="m-4 lg:m-0" />

            {/* Payment — iter65 rewrite.
                Wallet is an additive payment SOURCE, not a separate method.
                When checked + balance >= grand_total → no secondary method
                needed. When balance < grand_total → user picks COD/RAZORPAY
                for the remainder. The single "Place Order" button does
                the right thing on the backend (wallet debited + the rest
                billed via the chosen method).
            */}
            <section className="m-4 lg:m-0 bg-card rounded-2xl p-4 border border-border">
                <h3 className="font-bold text-sm mb-3">{t("payment_method", lang)}</h3>

                {walletBalance > 0 && (() => {
                    const walletCovers = walletBalance >= grandTotal;
                    const walletApplied = useWallet ? Math.min(walletBalance, grandTotal) : 0;
                    const remaining = Math.max(0, grandTotal - walletApplied);
                    return (
                        <div className="mb-3 rounded-xl border border-primary/30 bg-primary/5 overflow-hidden" data-testid="wallet-source-section">
                            <label className="flex items-center justify-between px-3 py-3 cursor-pointer">
                                <span className="inline-flex items-center gap-3">
                                    <Wallet size={18} className="text-primary" />
                                    <span>
                                        <span className="text-sm font-bold block">Use my Wallet</span>
                                        <span className="text-[11px] text-muted-foreground">Balance {inr2(walletBalance)}{walletCovers ? " — covers this order" : ""}</span>
                                    </span>
                                </span>
                                <input
                                    data-testid="use-wallet"
                                    type="checkbox"
                                    checked={useWallet}
                                    onChange={(e) => setUseWallet(e.target.checked)}
                                    className="w-5 h-5 accent-primary"
                                />
                            </label>
                            {useWallet && (
                                <div className="px-3 pb-3 pt-1 text-[12px] border-t border-primary/20 bg-card" data-testid="wallet-split">
                                    <div className="flex items-center justify-between py-1">
                                        <span className="text-muted-foreground">Wallet pays</span>
                                        <span className="font-bold text-primary" data-testid="wallet-amount">−{inr2(walletApplied)}</span>
                                    </div>
                                    {!walletCovers && (
                                        <div className="flex items-center justify-between py-1">
                                            <span className="text-muted-foreground">Remaining via {payment === "COD" ? "Cash" : "Online"}</span>
                                            <span className="font-bold" data-testid="wallet-remaining">{inr2(remaining)}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })()}

                {/* Only show the COD/Online picker when wallet isn't fully covering the order. */}
                {!(useWallet && walletBalance >= grandTotal) && (
                    <div className="space-y-2" data-testid="payment-method-picker">
                        <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1">
                            {useWallet ? "Pay remaining via" : "Pay via"}
                        </p>
                        {codAllowed && <PayOpt id="COD" current={payment} setPayment={setPayment} Icon={Banknote} label={t("cod", lang)} sub="Pay when order arrives" />}
                        <PayOpt id="RAZORPAY" current={payment} setPayment={setPayment} Icon={CreditCard} label={t("razorpay", lang)} sub={razorpayMock ? "UPI / Card / Netbanking (mocked)" : "UPI / Card / Netbanking"} />
                    </div>
                )}
            </section>

            {/* Notes */}
            <section className="m-4 lg:m-0 bg-card rounded-2xl p-4 border border-border">
                <h3 className="font-bold text-sm mb-3">Add instructions</h3>
                <textarea data-testid="notes-input" placeholder="No onion, less spicy…" value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full bg-muted rounded-xl px-3 py-2.5 text-sm outline-none" rows={2} />
            </section>

            </div>
            {/* Right column at lg+ — rewards + live summary. */}
            <div className="lg:space-y-4">

            {/* Coupons + Coins (stackable) */}
            <section className="m-4 lg:m-0 bg-card rounded-2xl p-4 border border-border" data-testid="rewards-section">
                <h3 className="font-bold text-sm mb-3 inline-flex items-center gap-2">
                    <BadgePercent size={16} className="text-primary" /> Offers &amp; Rewards
                </h3>

                {/* Coupon */}
                <div className="space-y-2 mb-4">
                    <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Coupon code</label>
                    {liveTotals.coupon_code && liveTotals.coupon_discount > 0 ? (
                        <div className="flex items-center justify-between gap-2 px-3 py-2.5 bg-emerald-50 border border-emerald-200 rounded-xl" data-testid="coupon-applied">
                            <div className="text-xs">
                                <span className="font-bold text-emerald-700 font-mono">{liveTotals.coupon_code}</span>
                                <span className="text-emerald-700 ml-2">applied · −{inr2(liveTotals.coupon_discount)} saved</span>
                            </div>
                            <button
                                type="button"
                                data-testid="coupon-remove"
                                onClick={async () => {
                                    setCouponInput(""); setCouponDraft("");
                                    // iter109 — also clear the persisted
                                    // cart.promo_code so it doesn't
                                    // silently rehydrate on the next
                                    // /cart or /order-placement request.
                                    try { await api.post("/coupons/remove"); } catch { /* soft-fail */ }
                                }}
                                className="p-1 text-emerald-700"
                            ><X size={14} /></button>
                        </div>
                    ) : (
                        <>
                            <button
                                type="button"
                                data-testid="coupon-browse"
                                onClick={openCouponPicker}
                                className="w-full flex items-center justify-between gap-2 px-3 py-3 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-xl text-left transition"
                            >
                                <span className="inline-flex items-center gap-2">
                                    <BadgePercent size={16} className="text-amber-600" />
                                    <span className="text-sm font-semibold text-amber-800">View available coupons</span>
                                </span>
                                <ChevronRight size={16} className="text-amber-600" />
                            </button>
                            <div className="flex items-center gap-2">
                                <input
                                    data-testid="coupon-input"
                                    value={couponInput}
                                    onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                                    placeholder="OR ENTER CODE MANUALLY"
                                    className="flex-1 bg-muted rounded-xl px-3 py-2 text-xs font-mono uppercase outline-none tracking-widest"
                                    maxLength={20}
                                />
                                <button
                                    type="button"
                                    data-testid="coupon-apply"
                                    onClick={() => setCouponDraft(couponInput.trim())}
                                    disabled={!couponInput.trim()}
                                    className="bg-primary text-primary-foreground rounded-xl px-3 py-2 text-xs font-bold disabled:opacity-50"
                                >Apply</button>
                            </div>
                        </>
                    )}
                    {couponDraft && !liveTotals.coupon_discount && (
                        <p className="text-[11px] text-rose-600" data-testid="coupon-invalid">
                            This code didn&apos;t apply — check spelling, expiry, or minimum cart value.
                        </p>
                    )}
                </div>

                {/* Coins */}
                <CoinsRedeemBlock
                    coinsBalance={coinsBalance}
                    coinsConfig={coinsConfig}
                    coinsToRedeem={coinsToRedeem}
                    setCoinsToRedeem={setCoinsToRedeem}
                    coinMax={coinMax}
                    coinMin={coinMin}
                    coinsDisc={liveTotals.coins_discount || 0}
                    subtotalForCap={Math.max(0, (liveTotals.subtotal || 0) - (liveTotals.coupon_kind === "FREE_DELIVERY" ? 0 : (liveTotals.coupon_discount || 0)))}
                />
            </section>

            {/* Live order summary — recomputed for the selected address */}
            {liveCart && (
                <section className="m-4 lg:m-0 bg-card rounded-2xl p-4 border border-border" data-testid="checkout-summary">
                    <h3 className="font-bold text-sm mb-3">Order summary</h3>
                    <div className="space-y-1.5 text-sm">
                        <Line k="Subtotal" v={inr2(liveTotals.subtotal || 0)} />
                        {liveTotals.packaging_fee > 0 && <Line k="Packaging" v={inr2(liveTotals.packaging_fee)} />}
                        <Line
                            k="Delivery fee"
                            v={liveTotals.delivery_fee === 0
                                ? <span className="text-emerald-600 font-semibold">FREE</span>
                                : inr2(liveTotals.delivery_fee)}
                        />
                        {liveTotals.delivery_distance_km != null && liveTotals.delivery_fee > 0 && (
                            <p className="text-[11px] text-muted-foreground -mt-0.5" data-testid="checkout-fee-breakdown">
                                {liveTotals.delivery_distance_km} km from shop · ₹{liveTotals.delivery_base_fee} base + ₹{liveTotals.delivery_per_km_fee}/km extra
                            </p>
                        )}
                        {liveTotals.gst_and_other_charges > 0 && <Line k="Taxes & charges" v={inr2(liveTotals.gst_and_other_charges)} />}
                        {liveTotals.coupon_discount > 0 && (
                            <Line
                                k={<span className="text-emerald-700 inline-flex items-center gap-1"><BadgePercent size={12} /> Coupon ({liveTotals.coupon_code})</span>}
                                v={<span className="text-emerald-700 font-bold" data-testid="summary-coupon-discount">−{inr2(liveTotals.coupon_discount)}</span>}
                            />
                        )}
                        {liveTotals.coins_discount > 0 && (
                            <Line
                                k={<span className="text-amber-700 inline-flex items-center gap-1"><Coins size={12} /> Coins ({liveTotals.coins_used})</span>}
                                v={<span className="text-amber-700 font-bold" data-testid="summary-coins-discount">−{inr2(liveTotals.coins_discount)}</span>}
                            />
                        )}
                        <div className="border-t border-border my-2"></div>
                        <Line k={<span className="font-bold">To Pay</span>} v={<span className="font-bold" data-testid="summary-grand-total">{inr2(grandTotal)}</span>} />
                        {(liveTotals.coupon_discount + liveTotals.coins_discount) > 0 && (
                            <p className="text-[11px] text-emerald-700 text-right font-semibold pt-0.5" data-testid="summary-savings">
                                You saved {inr2((liveTotals.coupon_discount || 0) + (liveTotals.coins_discount || 0))} on this order
                            </p>
                        )}
                    </div>
                </section>
            )}

            {/* Place order — iter178: below lg this is a ``fixed`` bottom bar, so where it sits in the DOM changes nothing on phones and tablets;
                from lg it is static — the last thing in the summary column, never floating over the bill. */}
            <button
                data-testid="place-order-btn"
                onClick={placeOrder}
                disabled={busy || !selectedAddr || liveTotals.delivery_out_of_zone || locationNeeded || !!lseBlock}
                className="fixed bottom-6 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] bg-primary text-primary-foreground rounded-2xl py-4 font-bold inline-flex items-center justify-center gap-2 shadow-2xl disabled:opacity-60 lg:static lg:translate-x-0 lg:flex lg:w-full lg:!max-w-none lg:shadow-none"
                style={{ maxWidth: "calc(var(--shell-width) - 32px)" }}
            >
                {busy && <Loader2 className="animate-spin" size={18} />}{" "}
                {liveTotals.delivery_out_of_zone
                    ? "Out of delivery zone"
                    : locationNeeded
                    ? "Set delivery location"
                    : (useWallet && walletBalance >= grandTotal)
                        ? <>{t("place_order", lang)} • Wallet {inr2(grandTotal)}</>
                        : <>{t("place_order", lang)} • {inr2(grandTotal)}</>}
            </button>

            </div>
            </div>

            {/* Can't-locate-this-address banner: no map pin AND the pincode matches no zone */}
            {locationNeeded && (
                <section
                    data-testid="checkout-location-needed-banner"
                    className="m-4 bg-amber-50 border border-amber-300 text-amber-900 rounded-2xl p-4 flex items-start gap-3"
                >
                    <MapPin size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 text-xs">
                        <p className="font-bold text-sm">Where exactly should we deliver?</p>
                        <p className="mt-1 opacity-90">
                            This address has no map location, and we don&apos;t recognise pincode <strong>{selectedAddr?.pincode}</strong>. Set your location on the map (or correct the pincode) so the shop and rider know where to go.
                        </p>
                        <button
                            data-testid="checkout-set-location"
                            onClick={() => setShowSetLocation(true)}
                            className="mt-2 bg-amber-600 text-white rounded-lg px-3 py-1.5 text-[11px] font-bold inline-flex items-center gap-1"
                        >Set location on map <ChevronRight size={11} /></button>
                    </div>
                </section>
            )}

            {/* Out-of-zone banner */}
            {liveTotals.delivery_out_of_zone && (
                <section
                    data-testid="checkout-out-of-zone-banner"
                    className="m-4 bg-rose-50 border border-rose-300 text-rose-800 rounded-2xl p-4 flex items-start gap-3"
                >
                    <AlertTriangle size={18} className="text-rose-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 text-xs">
                        <p className="font-bold text-sm">This shop doesn&apos;t deliver to this address.</p>
                        <p className="mt-1 opacity-90">
                            It&apos;s <strong>{liveTotals.delivery_distance_km} km away</strong> — the shop only delivers within <strong>{liveTotals.delivery_zone_radius_km} km</strong>. Pick a closer address to place the order.
                        </p>
                        <button
                            data-testid="checkout-out-of-zone-change"
                            onClick={() => setShowPicker(true)}
                            className="mt-2 bg-rose-600 text-white rounded-lg px-3 py-1.5 text-[11px] font-bold inline-flex items-center gap-1"
                        >Pick a different address <ChevronRight size={11} /></button>
                    </div>
                </section>
            )}


            {/* Picker drawer */}
            {showPicker && (
                <CheckoutAddressDrawer
                    addrList={addrList}
                    selectedAddrId={selectedAddrId}
                    onPick={(id) => { setSelectedAddrId(id); setShowPicker(false); }}
                    onAddNew={() => { setShowPicker(false); setShowAddNew(true); }}
                    onClose={() => setShowPicker(false)}
                />
            )}

            {/* Inline add-new */}
            {showAddNew && (
                <AddressPickerModal
                    onClose={() => setShowAddNew(false)}
                    onSaved={handleAddNew}
                />
            )}

            {/* Set-location: edit the selected address in place; saving re-runs loadAddrs so the
                new pin (or corrected pincode) immediately re-evaluates the banner above. */}
            {showSetLocation && selectedAddr && (
                <AddressPickerModal
                    initial={selectedAddr}
                    onClose={() => setShowSetLocation(false)}
                    onSaved={async () => { await loadAddrs(false); setShowSetLocation(false); }}
                />
            )}

            {/* Coupon picker */}
            {showCouponPicker && (
                <CouponPickerModal
                    busy={false}
                    available={availCoupons}
                    loading={couponPickerBusy}
                    onApply={applyCouponFromPicker}
                    onClose={() => setShowCouponPicker(false)}
                />
            )}
        </div>
    );
}

